import { memo } from 'react';
import { CellContext } from '@tanstack/react-table';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { CellProps } from 'components/DataTable/cells/types';
import {
  getCellNestedLevel,
  isProcurosAddedValue,
  useCellClassNames,
  useIsReadonly,
} from 'components/DataTable/cells/WebEDI/cellHelpers';
import { NestedLevelAddon } from 'components/DataTable/cells/WebEDI/components/NestedLevelAddon/NestedLevelAddon';
import { InputWrapperCell } from 'components/DataTable/cells/WebEDI/CreateEdit/InputWrapperCell';
import { WebEDIRowData } from 'components/DataTable/Types';
import { useFieldState } from 'components/SchemableForm/hooks/useFieldState';

import { CalculatedText } from './CalculatedText';
import { TextCellInput } from './TextCellInput';

export type Props = CellContext<WebEDIRowData, unknown> & CellProps;
// eslint-disable-next-line react/display-name
export const TextCell = memo(
  ({ fieldId, column, row, table, isHighlighted, mode }: Props) => {
    const {
      columnDef: { meta: columnMeta, ...columnDef },
      getIsFirstColumn,
      getIsLastColumn,
    } = column;
    const { service } = useErrorContext<WebEDIError>();
    const error = service.useErrorById(fieldId);

    const isSubmitting = table.options.meta?.isSubmitting ?? false;
    const isAdded = isProcurosAddedValue(row);
    const readOnly = useIsReadonly({ isAdded, mode, columnMeta, rowIndex: row.index, fieldId });
    const disabled = isSubmitting;
    const cellNestedLevel = columnMeta?.showNestLevel ? getCellNestedLevel(row) : 0;
    const cellClassNames = useCellClassNames({
      isLastColumn: getIsLastColumn(),
      isFirstColumn: getIsFirstColumn(),
      row,
      table,
    });

    const fieldState = useFieldState(fieldId, error);

    const leftAddon =
      columnMeta?.showNestLevel && cellNestedLevel > 1 ? <NestedLevelAddon level={cellNestedLevel} /> : null;

    if (columnMeta?.optionalConfig?.expression || columnMeta?.optionalConfig?.translateValue) {
      return (
        <InputWrapperCell
          fieldId={fieldId}
          isHighlighted={isHighlighted}
          className={cellClassNames}
          meta={columnMeta}
          fieldState={fieldState}
        >
          <CalculatedText
            fieldId={fieldId}
            readOnly={readOnly}
            disabled={disabled}
            error={error}
            cellClassNames={cellClassNames}
            leftAddon={leftAddon}
            isAdded={isAdded}
            row={row}
            columnDef={column.columnDef}
          />
        </InputWrapperCell>
      );
    }

    return (
      <InputWrapperCell
        fieldId={fieldId}
        isHighlighted={isHighlighted}
        className={cellClassNames}
        meta={columnMeta}
        fieldState={fieldState}
      >
        <TextCellInput
          error={error}
          quantityField={columnMeta?.optionalConfig?.quantityField ?? ''}
          isAdded={isAdded}
          accessorKey={'accessorKey' in columnDef ? (columnDef.accessorKey as string) : ''}
          readOnly={readOnly}
          disabled={disabled}
          leftAddon={leftAddon}
          isStatusColumn={Boolean(columnMeta?.optionalConfig?.isStatusColumn)}
          cellClassNames={cellClassNames}
          fieldId={fieldId}
          fieldState={fieldState}
        />
      </InputWrapperCell>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.row.id === nextProps.row.id && prevProps.isHighlighted === nextProps.isHighlighted;
  },
);
